import { CYBERSOURCE_ONLINE_GATEWAY_CODES, filterGatewayGroupByPrecedence, findGatewayByCode, HIDE_GATEWAY, PAY_PLUS_GATEWAY } from 'constants/TransactionGateways';
import { useTranslate } from 'context/TranslateContext';
import { chain } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { GlobalTypes } from 'redux/types';
import '../../layouts/card/GatewayCard.scss';
import { GatewayList } from './GatewayList';
import { useGateways } from './hooks';

export const PaymentGateway = () => {
  const {
    auth: { user, credentials },
  } = useSelector((state: GlobalTypes.RootState) => state);
  const { translate } = useTranslate();
  const [mccAvailableGatewayCodeList, getCredentials] = useGateways(user.company_id);
  if (mccAvailableGatewayCodeList.length === 0 && credentials.length === 0) {
    return <></>;
  }

  let isAppliedOnlineGateway = false;
  credentials.forEach((cred) => {
    const gateway = findGatewayByCode(cred.gateway_code);
    isAppliedOnlineGateway = isAppliedOnlineGateway || gateway?.gateway_type === 'online';
  });

  const availableApplyGatewayCodeList = mccAvailableGatewayCodeList
    .filter(
      (code) => !credentials.map((cred) => cred.gateway_code as string)?.includes(code),
    ) // exclude applied gateways
    .filter((code) => !HIDE_GATEWAY.includes(code)) // exclude hidden gateways
    .filter((code) => !PAY_PLUS_GATEWAY.includes(code)) //exclude pay-plus gateways displayed separately
    // Production test: Exclude Cybersource Online gateways
    .filter((code) => !CYBERSOURCE_ONLINE_GATEWAY_CODES.includes(code));

  const includedUnionpayAcqCredentials: string[] = credentials.filter((cred) =>
    ['16_1', '16_2', '16_3', '16_4', '16_5'].includes(cred.gateway_code)
  ).map((cred) => cred.gateway_code);

  const activatedUnionpayGatewayCodes: string[] = credentials.filter((cred) =>
    ['9_5', '9_6', '10_1', '10_2', '11_1', '11_2', '16_1', '16_2', '16_3', '16_4', '16_5'].includes(cred.gateway_code) &&
    cred.status == 'activated'
  ).map((cred) => cred.gateway_code);

  /**
   * For example,
   * 10_1 pending, if 16_2 is pending / activated, display 16_2
   * 10_1 activated, if 16_2 is activated, display 16_2
   * 10_1 activated, if 16_2 is pending, display 10_1
   */
  const codeList = credentials
    .filter((cred) => {
      switch (cred.gateway_code) {
        case '11_1':
        case '11_2':
          // Show one UnionPay Card
          if (cred.status == 'activated') {
            return !activatedUnionpayGatewayCodes.includes('16_1');
          }
          return !includedUnionpayAcqCredentials.includes('16_1');

        case '10_1':
          // Show one UnionPay Merchant Presented Code
          if (cred.status == 'activated') {
            return !activatedUnionpayGatewayCodes.includes('16_2');
          }
          return !includedUnionpayAcqCredentials.includes('16_2');

        case '10_2':
          // Show one UnionPay Customer Presented Code
          if (cred.status == 'activated') {
            return !activatedUnionpayGatewayCodes.includes('16_3');
          }
          return !includedUnionpayAcqCredentials.includes('16_3');

        case '9_5':
          // Show one UnionPay UPOP
          if (cred.status == 'activated') {
            return !activatedUnionpayGatewayCodes.includes('16_4');
          }
          return !includedUnionpayAcqCredentials.includes('16_4');

        case '9_6':
          // Show one UnionPay UPOP EMV Code
          if (cred.status == 'activated') {
            return !activatedUnionpayGatewayCodes.includes('16_5');
          }
          return !includedUnionpayAcqCredentials.includes('16_5');

        case '16_1':
          return cred.status == 'activated' ||
            (!activatedUnionpayGatewayCodes.includes('11_1') && !activatedUnionpayGatewayCodes.includes('11_2'));

        case '16_2':
          return cred.status == 'activated' || !activatedUnionpayGatewayCodes.includes('10_1');

        case '16_3':
          return cred.status == 'activated' || !activatedUnionpayGatewayCodes.includes('10_2');

        case '16_4':
          return cred.status == 'activated' || !activatedUnionpayGatewayCodes.includes('9_5');

        case '16_5':
          return cred.status == 'activated' || !activatedUnionpayGatewayCodes.includes('9_6');

        default:
          return !PAY_PLUS_GATEWAY.includes(cred.gateway_code);
      }
    })

  const codes = useMemo(
    () =>
      filterGatewayGroupByPrecedence(
        chain(codeList)
          .reject((cred) => PAY_PLUS_GATEWAY.includes(cred.gateway_code))
          // .uniqBy('gateway_sub_name')
          .map('gateway_code')
          .value(),
      ),
    [credentials],
  );
  return (
    <div id="gateway-content" className="hide-scrollbar" style={{ paddingBottom: '3rem' }}>
      {availableApplyGatewayCodeList.length > 0 && (
        <GatewayList
          title={translate('setup_payment_gateways')}
          caption={translate('choose_and_add_gateway')}
          codeList={availableApplyGatewayCodeList}
          isApplied={false}
          getCredentials={getCredentials}
          appliedOnlineGateway={isAppliedOnlineGateway}
          textOnly={false}
        />
      )}
      {credentials.length > 0 && (
        <GatewayList
          title={translate('my_gateways')}
          codeList={codes}
          isApplied={true}
          getCredentials={getCredentials}
          appliedOnlineGateway={isAppliedOnlineGateway}
          textOnly={availableApplyGatewayCodeList.length > 0}
        />
      )}
    </div>
  );
};
